"use client";

import {JSX} from "react";

import {FooterSimple} from "@/sections/common/footer/simple/footer-simple";

import {PointsMapView} from "@/features/rental/interactive-map";
import {PropertyFiltersView} from "@/features/rental/property-filters";

import {SearchResultPage} from "@/entities/rental";

import {useModel, useRouterBridge} from "@/shared/hooks";
import {useScrollTo} from "@/shared/hooks/dom/use-scroll-to";
import {useTranslation} from "@/shared/i18n";
import {Typography} from "@/shared/ui/data-display/typography";
import {Spinner} from "@/shared/ui/feedback/spinner";
import {Pagination} from "@/shared/ui/navigation/pagination";

import {ViewToggle} from "@/app/(search)/search/parts";
import {ListProperties} from "@/app/(search)/search/parts/list-properties";
import {NoticeEmpty} from "@/app/(search)/search/parts/notice-empty";

import {SearchPage} from "./search-page.model";

import "./styles/search-content.scss";

export function SearchContentView(props: SearchResultPage & {initialQueryString: string}): JSX.Element {
    const [pageModel, snapshot] = useModel(SearchPage, props);
    const {t} = useTranslation("search");

    useRouterBridge(pageModel, {skipInitialSync: true});

    const refScroll = useScrollTo<HTMLDivElement>(snapshot.isLoading);
    const refScrollMobile = useScrollTo<HTMLDivElement>(snapshot.isLoading);

    return (
        <div className="search-content">
            <div className="search-content__filters">
                <PropertyFiltersView model={pageModel.filters}>
                    <ViewToggle currentView={snapshot.viewMode} onViewChange={(view) => pageModel.onViewChange(view)} />
                </PropertyFiltersView>
            </div>

            <div className="search-content__layout" ref={refScrollMobile}>
                <div className={`search-content__left ${snapshot.viewMode === "list" ? "is-visible" : "is-hidden"}`} ref={refScroll}>
                    <div className="search-content__list">
                        <div className="search-content__header">
                            {snapshot.nameLocation && (
                                <Typography weight={600} size={18} color="title" as="h2">
                                    {t("vacationRentals", {name: snapshot.nameLocation})}
                                </Typography>
                            )}

                            {snapshot.pagination.total > 0 && (
                                <Typography weight={400} color="paragraph2" as="span">
                                    {t("rentalsRange", {
                                        start: snapshot.pagination.startIndex + 1,
                                        end: snapshot.pagination.endIndex,
                                        total: snapshot.pagination.total >= 300 ? "300+" : snapshot.pagination.total,
                                    })}
                                </Typography>
                            )}
                        </div>

                        {!snapshot.isLoading && snapshot.places.length === 0 && <NoticeEmpty pageModel={pageModel} />}

                        <div className="search-content__grid-wrapper">
                            {snapshot.isLoading && (
                                <>
                                    <div className="search-content__spinner-overlay" />
                                    <div className="search-content__spinner">
                                        <Spinner size="large" text={t("loadingProperties")} />
                                    </div>
                                </>
                            )}
                            <ListProperties model={pageModel} />
                        </div>

                        {!snapshot.isLoading &&
                            snapshot.pagination.total >= 300 &&
                            snapshot.pagination.current === snapshot.pagination.totalPages && (
                                <div className="search-content__limit-notice">
                                    <Typography weight={600} size={18} color="title" as="h3">
                                        {t("wantMoreOptions")}
                                    </Typography>
                                    <Typography weight={400} size={14} color="paragraph2" as="p">
                                        {t("adjustSearch")}
                                    </Typography>
                                </div>
                            )}

                        <Pagination model={pageModel.pagination} />
                    </div>
                    <FooterSimple />
                </div>

                <div className={`search-content__map ${snapshot.viewMode === "map" ? "is-visible" : "is-hidden"}`}>
                    <PointsMapView model={pageModel.map} isLoading={snapshot.isMapLoading} />
                </div>
            </div>
        </div>
    );
}
